import moment from "moment";
import { RevenueReconciliationReportConstant } from "presentation/constant/RevenueReconciliationReport/RevenueReconciliationReportConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useRevenueReconciliationReportVM } from "presentation/hook/RevenueReconciliationReport/useRevenueReconciliationReportVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useRevenueReconciliationReportTracked } from "presentation/store/RevenueReconciliationReport/RevenueReconciliationReportProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useCallback, useMemo, useState } from "react";
import { FieldType, GroupCheckboxList, HPHButton, HPHGroupCheckbox, IFieldValue, Loader, TimePicker } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction, StyledSidebar } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const RevenueReconciliationReportPanel = () => {
    const REVENUE_RECONCILIATION_REPORT_CONSTANT = RevenueReconciliationReportConstant.Table;
    const [revenueReconciliationReportState] = useRevenueReconciliationReportTracked();
    const revenueReconciliationReportVM = useRevenueReconciliationReportVM();
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();
    const [anainfoState] = useANAInfoTracked();
    const { operatingUnit, allowRead } = anainfoState;

    const { currentReport, reportIdTypeMap, masterState } = revenueReconciliationReportState;
    const { allFormState, isSaveClicked } = masterState;

    const handleReset = useCallback(() => {
        revenueReconciliationReportVM.onResetClick();
    }, [revenueReconciliationReportVM]);

    const getStartTime = (hours: number, min: number) => {
        const sysdate = moment();
        sysdate.hours(hours);
        sysdate.minutes(min);
        return sysdate
    }


    const handlePrint = useCallback(() => {
        if (currentReport.reportTypeList.length === 0) {
            messageBarVM.showError("Please select one Report at least.");
            return;
        }
        revenueReconciliationReportVM.onSaveDateTime();
        if (currentReport.cutOffDateFrom && currentReport.cutOffDateTo && currentReport.cutOffDateFrom > currentReport.cutOffDateTo) {
            messageBarVM.showError("Cut Off Date (To) should be later than or equals to Cut Off Date (From).");
            return;
        }
        if (currentReport.cutOffDate2ndFrom && currentReport.cutOffDate2ndTo && currentReport.cutOffDate2ndFrom > currentReport.cutOffDate2ndTo) {
            messageBarVM.showError("2ND Cut Off Date (To) should be later than or equals to 2ND Cut Off Date (From).");
            return;
        }
        if (currentReport.opsDateFrom && currentReport.opsDateTo && currentReport.opsDateFrom > currentReport.opsDateTo) {
            messageBarVM.showError("Ops Date (To) should be later than or equals to Ops Date (From).");
            return;
        }
        setIsLoading(true);
        revenueReconciliationReportVM.onSaveClicked();
        revenueReconciliationReportVM.onPrintClick(reportIdTypeMap, currentReport).then(data => {
            if (data && data.success) {
                setIsLoading(false);
                messageBarVM.showSuccess("Report is sent to printers successfully.");
            } else {
                setIsLoading(false);
                data.data && messageBarVM.showError(data.data ?? "Report print failed.");
            }
        });
    }, [currentReport, messageBarVM, reportIdTypeMap, revenueReconciliationReportVM]);


    const handleView = useCallback(() => {
        if (currentReport.reportTypeList.length === 0) {
            messageBarVM.showError("Please select one Report at least.");
            return;
        }
        revenueReconciliationReportVM.onSaveDateTime();
        if (currentReport.cutOffDateFrom && currentReport.cutOffDateTo && currentReport.cutOffDateFrom > currentReport.cutOffDateTo) {
            messageBarVM.showError("Cut Off Date (To) should be later than or equals to Cut Off Date (From).");
            return;
        }
        if (currentReport.cutOffDate2ndFrom && currentReport.cutOffDate2ndTo && currentReport.cutOffDate2ndFrom > currentReport.cutOffDate2ndTo) {
            messageBarVM.showError("2nd Cut Off Date (To) should be later than or equals to 2nd Cut Off Date (From).");
            return;
        }
        if (currentReport.opsDateFrom && currentReport.opsDateTo && currentReport.opsDateFrom > currentReport.opsDateTo) {
            messageBarVM.showError("Ops Date (To) should be later than or equals to Ops Date (From).");
            return;
        }
        setIsLoading(true);
        revenueReconciliationReportVM.onSaveClicked();
        revenueReconciliationReportVM.onViewClick(reportIdTypeMap, currentReport).then(data => {
            if (data && !data.success) {
                data.data && messageBarVM.showError(data.data ?? "Report View failed.");
            }
            setIsLoading(false);
        });
    }, [currentReport, messageBarVM, reportIdTypeMap, revenueReconciliationReportVM]);

    const memoPrinter = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "630px", marginBottom: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport.printer ?? ''}
                fieldValue={currentReport.printer}
                fieldLabel={REVENUE_RECONCILIATION_REPORT_CONSTANT.PRINTER}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'printer'}
                maxLength={60}
                options={revenueReconciliationReportState.dynamicOptions.printQueueDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    revenueReconciliationReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport.printer, REVENUE_RECONCILIATION_REPORT_CONSTANT.PRINTER, isSaveClicked, revenueReconciliationReportState.dynamicOptions.printQueueDropdownOptions, revenueReconciliationReportVM])

    const memoCurrency = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.currency ?? ''}
                fieldValue={currentReport?.currency}
                fieldLabel={REVENUE_RECONCILIATION_REPORT_CONSTANT.CURRRENCY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'currency'}
                maxLength={60}
                options={revenueReconciliationReportState.dynamicOptions.currencyDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    revenueReconciliationReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport?.currency, REVENUE_RECONCILIATION_REPORT_CONSTANT.CURRRENCY, isSaveClicked, revenueReconciliationReportState.dynamicOptions.currencyDropdownOptions, revenueReconciliationReportVM])

    const memoCashierId = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.cashierId ?? ''}
                fieldValue={currentReport?.cashierId}
                fieldLabel={REVENUE_RECONCILIATION_REPORT_CONSTANT.CASHIER_ID}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'cashierId'}
                maxLength={60}
                options={revenueReconciliationReportState.dynamicOptions.cashierIdDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    revenueReconciliationReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.cashierId, REVENUE_RECONCILIATION_REPORT_CONSTANT.CASHIER_ID, revenueReconciliationReportState.dynamicOptions.cashierIdDropdownOptions, revenueReconciliationReportVM])

    const memoSubType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.subType ?? ''}
                fieldValue={currentReport?.subType}
                fieldLabel={REVENUE_RECONCILIATION_REPORT_CONSTANT.SUB_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subType'}
                maxLength={60}
                options={revenueReconciliationReportState.dynamicOptions.subTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    revenueReconciliationReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.subType, REVENUE_RECONCILIATION_REPORT_CONSTANT.SUB_TYPE, revenueReconciliationReportState.dynamicOptions.subTypeDropdownOptions, revenueReconciliationReportVM])

    const memoCutOffDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_OFF_DATE_FROM}
                width="300px"
                date={currentReport?.cutOffDateFrom}
                fieldName="cutOffDateFrom"
                errorMessage={""}
                onDateChange={revenueReconciliationReportVM.onHeaderFieldChange} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_OFF_DATE_FROM, currentReport?.cutOffDateFrom, revenueReconciliationReportVM.onHeaderFieldChange])

    const memoCutOffTimeFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <TimePicker
                width='300px'
                maxLength={2}
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_OFF_TIME_FROM}
                type="number"
                value={getStartTime(currentReport?.cutOffTimeFromHour ?? 0, currentReport?.cutOffTimeFromMin ?? 0).toDate()}
                fieldName="cutOffTimeFrom"
                onChange={(val: any) => revenueReconciliationReportVM.onTimePickerChange(val, 'cutOffTimeFromHour', 'cutOffTimeFromMin')} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_OFF_TIME_FROM, currentReport?.cutOffTimeFromHour, currentReport?.cutOffTimeFromMin, revenueReconciliationReportVM])

    const memoCutOffDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_0FF_DATE_TO}
                width="300px"
                date={currentReport?.cutOffDateTo}
                fieldName="cutOffDateTo"
                errorMessage={""}
                onDateChange={revenueReconciliationReportVM.onHeaderFieldChange} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_0FF_DATE_TO, currentReport?.cutOffDateTo, revenueReconciliationReportVM.onHeaderFieldChange])

    const memoCutOffTimeTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <TimePicker
                width='300px'
                maxLength={2}
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_0FF_TIME_TO}
                type="number"
                value={getStartTime(currentReport?.cutOffTimeToHour ?? 23, currentReport?.cutOffTimeToMin ?? 59).toDate()}
                fieldName="cutOffTimeTo"
                onChange={(val: any) => revenueReconciliationReportVM.onTimePickerChange(val, 'cutOffTimeToHour', 'cutOffTimeToMin')} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_0FF_TIME_TO, currentReport?.cutOffTimeToHour, currentReport?.cutOffTimeToMin, revenueReconciliationReportVM])

    const memoOpsDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.OPS_DATE_FROM}
                width="300px"
                date={currentReport?.opsDateFrom}
                fieldName="opsDateFrom"
                errorMessage={""}
                onDateChange={revenueReconciliationReportVM.onHeaderFieldChange} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.OPS_DATE_FROM, currentReport?.opsDateFrom, revenueReconciliationReportVM.onHeaderFieldChange])

    const memoOpsTimeFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <TimePicker
                width='300px'
                maxLength={2}
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.OPS_TIME_FROM}
                type="number"
                value={getStartTime(currentReport?.opsTimeFromHour ?? 0, currentReport?.opsTimeFromMin ?? 0).toDate()}
                fieldName="opsTimeFrom"
                onChange={(val: any) => revenueReconciliationReportVM.onTimePickerChange(val, 'opsTimeFromHour', 'opsTimeFromMin')} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.OPS_TIME_FROM, currentReport?.opsTimeFromHour, currentReport?.opsTimeFromMin, revenueReconciliationReportVM])


    const memoOpsDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.OPS_DATE_TO}
                width="300px"
                date={currentReport?.opsDateTo}
                fieldName="opsDateTo"
                errorMessage={""}
                onDateChange={revenueReconciliationReportVM.onHeaderFieldChange} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.OPS_DATE_TO, currentReport?.opsDateTo, revenueReconciliationReportVM.onHeaderFieldChange])

    const memoOpsTimeTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <TimePicker
                width='300px'
                maxLength={2}
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.OPS_TIME_TO}
                type="number"
                value={getStartTime(currentReport?.opsTimeToHour ?? 23, currentReport?.opsTimeToMin ?? 59).toDate()}
                fieldName="opsTimeTo"
                onChange={(val: any) => revenueReconciliationReportVM.onTimePickerChange(val, 'opsTimeToHour', 'opsTimeToMin')} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.OPS_TIME_TO, currentReport?.opsTimeToHour, currentReport?.opsTimeToMin, revenueReconciliationReportVM])


    const memoCutOffDate2ndFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_OFF_DATE_2ND_FROM}
                width="300px"
                date={currentReport?.cutOffDate2ndFrom}
                fieldName="cutOffDate2ndFrom"
                errorMessage={""}
                onDateChange={revenueReconciliationReportVM.onHeaderFieldChange} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_OFF_DATE_2ND_FROM, currentReport?.cutOffDate2ndFrom, revenueReconciliationReportVM.onHeaderFieldChange])

    const memoCutOffTime2ndFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <TimePicker
                width='300px'
                maxLength={2}
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_OFF_TIME_2ND_FROM}
                type="number"
                value={getStartTime(currentReport?.cutOffTime2ndFromHour ?? 0, currentReport?.cutOffTime2ndFromMin ?? 0).toDate()}
                fieldName="cutOffTime2ndFrom"
                onChange={(val: any) => revenueReconciliationReportVM.onTimePickerChange(val, 'cutOffTime2ndFromHour', 'cutOffTime2ndFromMin')} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_OFF_TIME_2ND_FROM, currentReport?.cutOffTime2ndFromHour, currentReport?.cutOffTime2ndFromMin, revenueReconciliationReportVM])
    const memoCutOffDate2ndTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_0FF_DATE_2ND_TO}
                width="300px"
                date={currentReport?.cutOffDate2ndTo}
                fieldName="cutOffDate2ndTo"
                errorMessage={""}
                onDateChange={revenueReconciliationReportVM.onHeaderFieldChange} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_0FF_DATE_2ND_TO, currentReport?.cutOffDate2ndTo, revenueReconciliationReportVM.onHeaderFieldChange])

    const memoCutOffTime2ndTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <TimePicker
                width='300px'
                maxLength={2}
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_0FF_TIME_2ND_TO}
                type="number"
                value={getStartTime(currentReport?.cutOffTime2ndToHour ?? 23, currentReport?.cutOffTime2ndToMin ?? 59).toDate()}
                fieldName="cutOffTime2ndTo"
                onChange={(val: any) => revenueReconciliationReportVM.onTimePickerChange(val, 'cutOffTime2ndToHour', 'cutOffTime2ndToMin')} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.CUT_0FF_TIME_2ND_TO, currentReport?.cutOffTime2ndToHour, currentReport?.cutOffTime2ndToMin, revenueReconciliationReportVM])

    const memoDocDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.DOC_DATE}
                width="300px"
                date={currentReport?.docDate}
                fieldName="docDate"
                errorMessage={""}
                onDateChange={revenueReconciliationReportVM.onHeaderFieldChange} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.DOC_DATE, currentReport?.docDate, revenueReconciliationReportVM.onHeaderFieldChange])

    const memoDocDateTime = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "15px" }}>
            <TimePicker
                width='300px'
                maxLength={2}
                disabled={false}
                optional={true}
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.DOC_DATE_TIME}
                type="number"
                value={getStartTime(currentReport?.docDateTimeHour ?? 0, currentReport?.docDateTimeMin ?? 0).toDate()}
                fieldName="docDateTime"
                onChange={(val: any) => revenueReconciliationReportVM.onTimePickerChange(val, 'docDateTimeHour', 'docDateTimeMin')} />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.DOC_DATE_TIME, currentReport?.docDateTimeHour, currentReport?.docDateTimeMin, revenueReconciliationReportVM])


    const memoReportCheckBox = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "630px", marginBottom: "15px" }}>
            <HPHGroupCheckbox
                label={REVENUE_RECONCILIATION_REPORT_CONSTANT.REPORT_TYPE}
                checkboxData={revenueReconciliationReportState.reportTypeCheckboxOptions}
                selectedValues={currentReport.reportTypeList}
                orientation={"vertical"}
                onChange={(e: (GroupCheckboxList | undefined)[]) => revenueReconciliationReportVM.onGroupCheckboxChange(e)}
            />
        </div>
        , [REVENUE_RECONCILIATION_REPORT_CONSTANT.REPORT_TYPE, currentReport.reportTypeList, revenueReconciliationReportState.reportTypeCheckboxOptions, revenueReconciliationReportVM])

    return <>

        <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
            <HeaderTitle>{RevenueReconciliationReportConstant.Title.TITLE}</HeaderTitle>
            {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
            <StyledAction className="tm-animated-wrapper">
                <HPHButton label={WorkspaceConstant.Common.BUTTON_CLEAR} size={"Small"} theme={"Secondary"} disabled={false} onClick={handleReset} />
                {allowRead && <><div className="add-seperator" />
                    <HPHButton label={WorkspaceConstant.Common.BUTTON_VIEW} size={"Small"} theme={"Secondary"} disabled={false} onClick={handleView} />
                    <HPHButton label={WorkspaceConstant.Common.BUTTON_PRINT} size={"Small"} theme={"Primary"} disabled={false} onClick={handlePrint} /></>}
            </StyledAction>
        </Sidebarheader>
        <StyledSidebar style={{ width: '100%', height: "100%", border: 'none', backgroundColor: '#FFFFFF', overflow: 'auto', padding: '0' }} position='right'>

            <div className={'add-edit-form'} style={{ height: 'calc(100vh - 125px)', maxHeight: 'calc(100vh - 125px)', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {memoPrinter}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCutOffDateFrom}
                            {memoCutOffTimeFrom}
                            {memoCutOffDateTo}
                            {memoCutOffTimeTo}
                        </CriteriaItemContainer>

                        {(operatingUnit === 'OICT' || operatingUnit === "AQCT" || operatingUnit === "RSCT") && <>
                            <CriteriaItemContainer>
                                {memoOpsDateFrom}
                                {memoOpsTimeFrom}
                                {memoOpsDateTo}
                                {memoOpsTimeTo}
                            </CriteriaItemContainer>
                        </>}

                        <CriteriaItemContainer>
                            {memoCurrency}
                            {memoCashierId}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCutOffDate2ndFrom}
                            {memoCutOffTime2ndFrom}
                            {memoCutOffDate2ndTo}
                            {memoCutOffTime2ndTo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoDocDate}
                            {memoDocDateTime}
                            {(operatingUnit === 'SAPT' || operatingUnit === "KICT") && memoSubType}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoReportCheckBox}
                        </CriteriaItemContainer>


                    </div>
                </div>
            </div>
        </StyledSidebar >
    </>
}

export default memo(RevenueReconciliationReportPanel);
